import { useEffect } from 'react'
import { getTenantBySlug } from './api.js'
import { useBrandingStore, type TenantBranding } from '../stores/branding.store.js'

/**
 * Resuelve el branding del tenant a partir del slug de la URL `/r/:slug/...`.
 *
 * - Marca el slug como activo (lo lee el `NintaiGOHeader` para elegir qué
 *   nombre mostrar).
 * - Si no hay branding cacheado para este slug, dispara la llamada al endpoint
 *   público `getTenantBySlug` y lo guarda.
 * - Si la llamada falla, el header cae al fallback "NintaiGO" — el comensal
 *   nunca queda sin marca (memo del usuario).
 *
 * Retorna el branding actual (puede ser `null` mientras carga la primera vez).
 */
export function useTenantBranding(slug: string | null | undefined): TenantBranding | null {
  const branding = useBrandingStore((s) => (slug ? s.bySlug[slug] ?? null : null))
  const setBranding = useBrandingStore((s) => s.setBranding)
  const setActiveSlug = useBrandingStore((s) => s.setActiveSlug)

  useEffect(() => {
    setActiveSlug(slug ?? null)
  }, [slug, setActiveSlug])

  useEffect(() => {
    if (!slug) return
    // Si ya está cacheado lo dejamos — el endpoint es estable y barato; el
    // refresco lo dispara un logout o un slug nuevo.
    if (branding) return
    let alive = true
    void (async () => {
      const r = await getTenantBySlug(slug)
      if (!alive) return
      if (r.ok) {
        // `accentColor` aún no viene en el response público — leemos optional
        // para que cuando el endpoint se extienda el guest lo recoja automático.
        const accentColor = (r.data as unknown as { accentColor?: string | null }).accentColor ?? null
        setBranding({ slug, tenantId: r.data.tenantId, name: r.data.name, accentColor })
      }
      // Si falla, no escribimos nada: el header usará el fallback "NintaiGO".
    })()
    return () => {
      alive = false
    }
  }, [slug, branding, setBranding])

  // Aplicar `--brand-accent` al <html> si el tenant trae color custom. El CSS
  // ya hace fallback a `var(--brand)` (verde NintaiGO) cuando no está seteado.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    const accent = branding?.accentColor
    if (accent && /^#[0-9a-fA-F]{3,8}$/.test(accent)) {
      root.style.setProperty('--brand-accent', accent)
    } else {
      root.style.removeProperty('--brand-accent')
    }
  }, [branding?.accentColor])

  return branding
}
